import { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/App';

// Shared hook: loads /api/settings, tracks the logged-in user, and provides
// a debounced queueSave that persists updates back to the backend.
export function useEditableSettings() {
  const [settings, setSettings] = useState(null);
  const [user, setUser] = useState(null);
  const timer = useRef(null);

  useEffect(() => {
    axios.get(`${API}/settings`).then(r => setSettings(r.data)).catch(() => {});
    const token = localStorage.getItem('session_token');
    if (token) {
      axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => setUser(r.data))
        .catch(() => {
          localStorage.removeItem('session_token');
          localStorage.removeItem('user');
        });
    }
  }, []);

  const queueSave = useCallback((next) => {
    setSettings(next);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      const token = localStorage.getItem('session_token');
      if (!token) return;
      try {
        await axios.put(`${API}/settings`, next, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Saved', { duration: 900 });
      } catch (e) {
        toast.error('Save failed');
      }
    }, 700);
  }, []);

  const updateLabel = useCallback((key, value) => {
    if (!settings) return;
    queueSave({ ...settings, labels: { ...(settings.labels || {}), [key]: value } });
  }, [settings, queueSave]);

  const updateField = useCallback((key, value) => {
    if (!settings) return;
    queueSave({ ...settings, [key]: value });
  }, [settings, queueSave]);

  return { settings, user, isAdmin: !!user, queueSave, updateLabel, updateField, setUser };
}
